'use client';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowLeft, ArrowRight, BookOpen, Clock3, Plus } from 'lucide-react';
import type { AlbumSummary } from '@freealbum/shared';
import { Link } from '@/i18n/navigation';
import { LinkIcon } from '@/components/ui/link-status';
import { cn, formatDate } from '@/lib/utils';

/** A familiar book shape, even before an album has its first uploaded photo. */
function AlbumPreview({ album, featured = false }: { album: AlbumSummary; featured?: boolean }) {
  return (
    <div aria-hidden className={cn('relative shrink-0 overflow-hidden rounded-e-md rounded-s-sm border border-ink/10 bg-paper-2 shadow-sm',
      featured ? 'aspect-square w-24 sm:w-36 lg:w-48' : 'size-16')}>
      {album.coverThumbUrl ? (
        <img src={album.coverThumbUrl} alt="" loading={featured ? 'eager' : 'lazy'} decoding="async" className="size-full object-cover" />
      ) : (
        <div className="flex size-full items-center justify-center bg-accent-soft text-accent">
          <div className="absolute inset-2 rounded-sm border border-accent/15" />
          <BookOpen className={featured ? 'size-9' : 'size-6'} strokeWidth={1.25} />
        </div>
      )}
      <span className="absolute inset-y-0 start-0 w-2 border-e border-white/30 bg-ink/10" />
    </div>
  );
}

export function RecentAlbums({ albums }: { albums: AlbumSummary[] }) {
  const t = useTranslations('home.recent');
  const ta = useTranslations('albums');
  const tf = useTranslations('formats');
  const locale = useLocale();
  const Arrow = locale === 'he' ? ArrowLeft : ArrowRight;
  const [latest, ...others] = albums;
  if (!latest) return null;
  const title = (album: AlbumSummary) => album.title || t('untitled');
  const metadata = (album: AlbumSummary) => `${ta('card.pagesCount', { count: album.innerPages })} · ${ta('card.photosCount', { count: album.photoCount })}`;

  return (
    <section aria-labelledby="recent-albums-title" className="mx-auto max-w-6xl px-gutter py-8 md:py-12 lg:px-gutter-lg">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 md:mb-8">
        <div>
          <p className="mb-2 text-xs font-semibold tracking-wide text-accent">{t('eyebrow')}</p>
          <h1 id="recent-albums-title" className="font-display text-3xl leading-tight tracking-display text-balance text-ink md:text-4xl">{t('title')}</h1>
          <p className="mt-2 text-sm text-ink-2">{t('lead')}</p>
        </div>
        <Link href="/albums/new" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-ink shadow-xs hover:bg-paper-2">
          <LinkIcon><Plus aria-hidden className="size-4" /></LinkIcon>{t('newAlbum')}
        </Link>
      </div>

      <div className={cn('grid gap-6', others.length > 0 && 'lg:grid-cols-[1.2fr_1fr]')}>
        <Link href={`/albums/${latest.id}/edit`} aria-label={t('open', { title: title(latest) })}
          className="group flex min-w-0 flex-col overflow-hidden rounded-xl border border-accent/25 bg-surface shadow-sm transition-shadow hover:shadow-md">
          <div className="flex items-center gap-2 border-b border-accent/10 bg-accent-soft/50 px-5 py-3 text-xs font-medium text-accent">
            <Clock3 aria-hidden className="size-3.5" />{t('lastEdited')}
          </div>
          <div className="flex flex-1 items-center gap-4 p-5 sm:gap-6 sm:p-6">
            <AlbumPreview album={latest} featured />
            <div className="min-w-0">
              <h2 className="k-nikud line-clamp-2 break-words font-display text-xl leading-snug text-ink sm:text-2xl">{title(latest)}</h2>
              <p className="mt-2 text-xs text-ink-2">{metadata(latest)}</p>
              <p className="mt-1 text-xs text-ink-3">{tf(latest.format)}</p>
              <p className="mt-3 text-xs text-ink-3"><time dateTime={latest.updatedAt}>{ta('card.updated', { date: formatDate(latest.updatedAt, locale) })}</time></p>
            </div>
          </div>
          <div className="px-5 pb-5 sm:px-6 sm:pb-6">
            <span className="flex min-h-12 items-center justify-center gap-2 rounded-md bg-accent px-4 text-sm font-semibold text-accent-fg shadow-xs group-hover:bg-accent-hover">
              {t('continue')}<LinkIcon><Arrow aria-hidden className="size-4" /></LinkIcon>
            </span>
          </div>
        </Link>

        {others.length > 0 ? (
          <div className="min-w-0">
            <h2 className="mb-3 text-sm font-semibold text-ink">{t('otherAlbums')}</h2>
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
              {others.map((album) => (
                <li key={album.id}>
                  <Link href={`/albums/${album.id}/edit`} aria-label={t('open', { title: title(album) })}
                    className="flex items-center gap-3 p-3 transition-colors hover:bg-paper-2 sm:p-4">
                    <AlbumPreview album={album} />
                    <div className="min-w-0 flex-1">
                      <h3 className="k-nikud line-clamp-2 break-words font-display text-base leading-snug text-ink">{title(album)}</h3>
                      <p className="mt-1 text-xs text-ink-2">{metadata(album)}</p>
                      <p className="mt-1 text-2xs text-ink-3"><time dateTime={album.updatedAt}>{ta('card.updated', { date: formatDate(album.updatedAt, locale) })}</time></p>
                    </div>
                    <LinkIcon className="shrink-0 text-ink-3"><Arrow aria-hidden className="size-4 shrink-0 text-ink-3" /></LinkIcon>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      <div className="mt-4 flex justify-end">
        <Link href="/albums" className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm font-medium text-accent hover:bg-accent-soft">
          {t('allAlbums')}<LinkIcon><Arrow aria-hidden className="size-4" /></LinkIcon>
        </Link>
      </div>
    </section>
  );
}
